import { useSensorDataAvailability } from 'features/timeSeries/hooks/useSensorDataAvailability'
import { useMemo } from 'react'
import { BaseSensorEntry } from 'types'
import { z } from 'zod'

export const ALL_SOURCES = '__all__'
export const MIN_SEARCH_LENGTH = 3

export const dataFilterSchema = z.enum(['all', 'has', 'missing'])
export type DataFilter = z.infer<typeof dataFilterSchema>

export const useTagSourceOptions = (tags: BaseSensorEntry[]): string[] =>
  useMemo(() => {
    const found = new Set<string>()
    tags.forEach((tag) => {
      if (tag.data.point_source) found.add(tag.data.point_source)
    })
    return Array.from(found).sort((a, b) => a.localeCompare(b))
  }, [tags])

interface AvailabilityFilteredTags {
  displayed: BaseSensorEntry[]
  isCheckingAvailability: boolean
}

/* The availability probe is gated on a meaningful search so a bare filter
   never surveys the whole site's tag list. */
export const useAvailabilityFilteredTags = (
  tags: BaseSensorEntry[],
  source: string,
  dataFilter: DataFilter,
  hasMeaningfulSearch: boolean
): AvailabilityFilteredTags => {
  const sourceFiltered = useMemo(
    () =>
      source === ALL_SOURCES
        ? tags
        : tags.filter((tag) => tag.data.point_source === source),
    [tags, source]
  )
  const dataFilterActive = dataFilter !== 'all' && hasMeaningfulSearch
  const availabilitySurveyTagIds = useMemo(
    () => (dataFilterActive ? sourceFiltered.map((tag) => tag.id) : []),
    [dataFilterActive, sourceFiltered]
  )
  const { hasRecentDataBySensorId, isLoading } = useSensorDataAvailability(
    availabilitySurveyTagIds
  )
  const displayed = useMemo(() => {
    if (!dataFilterActive) return sourceFiltered
    return sourceFiltered.filter((tag) =>
      dataFilter === 'has'
        ? hasRecentDataBySensorId[tag.id] !== false
        : hasRecentDataBySensorId[tag.id] === false
    )
  }, [sourceFiltered, dataFilterActive, dataFilter, hasRecentDataBySensorId])

  return { displayed, isCheckingAvailability: isLoading }
}

/** Selection toggle with the pen-cap guard, shared by every tag list. */
export const toggleTagSelection = (
  prev: BaseSensorEntry[],
  tag: BaseSensorEntry,
  cap: number
): BaseSensorEntry[] => {
  const isSelected = prev.some((entry) => entry.id === tag.id)
  if (isSelected) return prev.filter((entry) => entry.id !== tag.id)
  if (prev.length >= cap) return prev
  return [...prev, tag]
}

export const emptyTagListCopy = (
  dataFilter: DataFilter,
  hasMeaningfulSearch: boolean
): string =>
  dataFilter !== 'all' && !hasMeaningfulSearch
    ? t`Type ${MIN_SEARCH_LENGTH}+ characters to check availability.`
    : t`No tags match.`
import { t } from '@lingui/core/macro'
